import { ActionBuilder, NamespaceBuilder } from '@hub3js/core';
import { actions, builders, utils as evmUtils } from '@hub3js/evm';
import * as commonBuilders from '@hub3js/std/builders';
import { privyAdapter } from '../../adapter';
import { SOCIAL_WALLET_TYPE } from '../constants';
import { getEmbeddedEvmProvider, getEmbeddedEvmWallet } from '../utils';
export function buildEvmNamespace() {
    const evmNamespace = new NamespaceBuilder('EVM', SOCIAL_WALLET_TYPE);
    evmNamespace.action(new ActionBuilder('connect')
        .action(async (_context, chain) => {
        const privyContext = privyAdapter.getPrivyContext();
        const instance = (await getEmbeddedEvmWallet(privyContext.privyEvmWallets)?.getEthereumProvider());
        if (!privyContext.ready) {
            throw new Error('Privy is not ready!');
        }
        if (!instance) {
            throw new Error('Privy instance is not available!');
        }
        if (!privyContext.user) {
            throw new Error('User is not logged in!');
        }
        if (chain) {
            const currentChainId = await instance.request({
                method: 'eth_chainId',
            });
            const supportedChains = privyAdapter.getSupportedChains();
            const requestedChainId = typeof chain === 'string'
                ? supportedChains.find((blockchain) => blockchain.name === chain)?.chainId
                : chain.chainId;
            if (requestedChainId && requestedChainId !== currentChainId) {
                await instance.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: requestedChainId }],
                });
            }
        }
        const evmResult = await evmUtils.getAccounts(instance);
        const formattedAccounts = evmUtils.formatAccountsToCAIP(evmResult.accounts, evmResult.chainId);
        if (formattedAccounts.length === 0) {
            throw new Error('No accounts found');
        }
        return {
            accounts: formattedAccounts,
            network: evmResult.chainId,
        };
    })
        .and((context, accounts) => {
        const [, setState] = context.state();
        setState('accounts', accounts.accounts);
        setState('network', accounts.network);
        setState('connected', true);
    })
        .or(() => {
        const privyContext = privyAdapter.getPrivyContext();
        privyContext.login();
    })
        .build());
    evmNamespace.action(builders.canSwitchNetwork().action(actions.canSwitchNetwork()).build());
    evmNamespace.action(builders
        .getChainId()
        .action(async () => {
        const instance = await getEmbeddedEvmProvider();
        if (!instance) {
            throw new Error('Trying to get chain id from your EVM wallet, but instance is not available.');
        }
        return instance.request({ method: 'eth_chainId' });
    })
        .build());
    evmNamespace.action(commonBuilders
        .disconnect()
        .and(() => {
        const privyContext = privyAdapter.getPrivyContext();
        if (!!privyContext.user) {
            void privyContext.logout();
        }
    })
        .build());
    evmNamespace.action(builders
        .getAllowance()
        .action(async (context, params) => {
        const instance = await getEmbeddedEvmProvider();
        if (!instance) {
            throw new Error('Trying to get allowance from your EVM wallet, but instance is not available.');
        }
        return actions.getAllowance(() => instance)(context, params);
    })
        .build());
    evmNamespace.action(builders
        .getTransactionReceipt()
        .action(async (context, txHash) => {
        const instance = await getEmbeddedEvmProvider();
        if (!instance) {
            throw new Error('Trying to get transaction receipt from your EVM wallet, but instance is not available.');
        }
        return actions.getTransactionReceipt(() => instance)(context, txHash);
    })
        .build());
    return evmNamespace.build();
}
